/**
 * Error Logging Service
 * Captures and logs application errors to Supabase
 */

import { supabase } from '../supabase';

// Error queue for offline logging
const ERROR_QUEUE_KEY = 'lavvissa_error_queue';
const MAX_QUEUE_SIZE = 50;

// Flag to prevent recursive error logging
let isLoggingError = false;
const ERROR_LOGGER_PREFIX = '__ERROR_LOGGER__';

// Flag to track if error_logs table is unavailable (prevents repeated 401 requests)
let errorLogsUnavailable = false;

/**
 * Get device and browser information
 */
const getDeviceInfo = () => {
  try {
    const ua = navigator.userAgent;
    const platform = navigator.platform;
    const language = navigator.language;
    const screen = {
      width: window.screen?.width || null,
      height: window.screen?.height || null,
    };
    const viewport = {
      width: window.innerWidth || null,
      height: window.innerHeight || null,
    };

    return {
      userAgent: ua,
      platform,
      language,
      screen,
      viewport,
      cookieEnabled: navigator.cookieEnabled,
      onLine: navigator.onLine,
      timestamp: new Date().toISOString(),
    };
  } catch (error) {
    // Silently fail - don't log errors from device info fetching
    return {};
  }
};

/**
 * Get current user context from Supabase auth
 */
const getUserContext = async () => {
  try {
    const { data: { user }, error } = await supabase.auth.getUser();
    if (error) throw error;

    if (user) {
      // Get user email from auth
      const userEmail = user.email;
      
      // Try to get additional user data from users table
      const { data: userData } = await supabase
        .from('users')
        .select('email, display_name')
        .eq('id', user.id)
        .single();

      return {
        userId: user.id,
        userEmail: userEmail || userData?.email || null,
        displayName: userData?.display_name || null,
      };
    }
    return { userId: null, userEmail: null, displayName: null };
  } catch (error) {
    // Silently fail - don't log errors from user context fetching
    // This prevents infinite loops if auth fails
    return { userId: null, userEmail: null, displayName: null };
  }
};

/**
 * Determine error severity based on error type and message
 */
const determineSeverity = (error, errorType) => {
  const message = error?.message || error?.toString() || '';
  const lowerMessage = message.toLowerCase();

  // Critical errors
  if (
    errorType === 'unhandled' ||
    errorType === 'promise_rejection' ||
    lowerMessage.includes('network') ||
    lowerMessage.includes('failed to fetch') ||
    lowerMessage.includes('timeout') ||
    (error?.status && error.status >= 500)
  ) {
    return 'critical';
  }

  // Warnings
  if (
    lowerMessage.includes('warning') ||
    lowerMessage.includes('deprecated') ||
    (error?.status && error.status >= 400 && error.status < 500)
  ) {
    return 'warning';
  }

  // Default to error
  return 'error';
};

/**
 * Clean error data to match database schema
 * Removes any fields that don't exist in the error_logs table
 */
const cleanErrorData = (errorData) => {
  // Define allowed fields based on database schema
  const allowedFields = [
    'error_message',
    'error_stack',
    'error_type',
    'severity',
    'user_id',
    'user_email',
    'url',
    'user_agent',
    'device_info',
    'component_stack',
    'api_endpoint',
    'api_status',
    'request_data',
    'resolved',
    'resolved_at',
    'resolved_by',
  ];

  const cleaned = {};
  for (const field of allowedFields) {
    if (errorData[field] !== undefined && errorData[field] !== null) {
      cleaned[field] = errorData[field];
    }
  }

  return cleaned;
};

/**
 * Save error to queue (for offline scenarios)
 */
const queueError = (errorData) => {
  try {
    const queue = JSON.parse(localStorage.getItem(ERROR_QUEUE_KEY) || '[]');
    queue.push({
      ...errorData,
      queuedAt: new Date().toISOString(),
    });

    // Limit queue size
    if (queue.length > MAX_QUEUE_SIZE) {
      queue.shift(); // Remove oldest
    }

    localStorage.setItem(ERROR_QUEUE_KEY, JSON.stringify(queue));
  } catch (error) {
    // Silently fail - don't log errors from queueing
    // This prevents infinite loops
  }
};

/**
 * Process queued errors (call this when app comes online)
 */
export const processErrorQueue = async () => {
  try {
    const queue = JSON.parse(localStorage.getItem(ERROR_QUEUE_KEY) || '[]');
    if (queue.length === 0) return;

    const errorsToProcess = [...queue];
    // Clear the queue immediately to prevent re-processing
    localStorage.removeItem(ERROR_QUEUE_KEY);

    for (const errorData of errorsToProcess) {
      try {
        // Clean the error data before inserting (removes queuedAt and other invalid fields)
        const cleanedData = cleanErrorData(errorData);
        await logErrorToSupabase(cleanedData);
      } catch (error) {
        // If logging fails, don't re-queue to prevent infinite loops
        // The error is already logged, no need to keep retrying
      }
    }
  } catch (error) {
    // Silently fail - don't log errors from processing queue
    // This prevents infinite loops
    // Clear the queue if there's a parsing error
    try {
      localStorage.removeItem(ERROR_QUEUE_KEY);
    } catch (e) {
      // Ignore
    }
  }
};

/**
 * Log error directly to Supabase
 */
const logErrorToSupabase = async (errorData) => {
  // If we already know the error_logs table is unavailable, skip entirely
  if (errorLogsUnavailable) {
    return null;
  }

  try {
    // Clean the error data to remove any extra fields (like queuedAt)
    const cleanedData = cleanErrorData(errorData);

    // Try using Supabase client first (works if authenticated)
    try {
      const { data, error } = await supabase
        .from('error_logs')
        .insert([cleanedData])
        .select()
        .single();

      if (!error && data) {
        return data;
      }
      // If the table doesn't exist or we get a permission error, mark as unavailable
      if (error) {
        const errMsg = error.message || '';
        const errCode = error.code || '';
        if (errCode === '42P01' || errCode === 'PGRST204' || errCode === '42501' ||
            errMsg.includes('does not exist') || errMsg.includes('permission denied') ||
            errMsg.includes('401') || errMsg.includes('403')) {
          errorLogsUnavailable = true;
          return null;
        }
      }
      // If error, fall through to direct fetch
    } catch (clientError) {
      // Fall through to direct fetch if client fails
    }

    // Use a direct fetch to bypass potential RLS issues
    // This ensures errors can be logged even when unauthenticated
    const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || process.env.VITE_SUPABASE_URL;
    const supabaseKey = process.env.REACT_APP_SUPABASE_ANON_KEY || process.env.VITE_SUPABASE_ANON_KEY;

    if (!supabaseUrl || !supabaseKey) {
      // Silently fail if credentials are not configured
      return null;
    }

    const response = await fetch(`${supabaseUrl}/rest/v1/error_logs`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'apikey': supabaseKey,
        'Authorization': `Bearer ${supabaseKey}`,
        'Prefer': 'return=representation'
      },
      body: JSON.stringify(cleanedData)
    });

    // Silently handle 401 (unauthorized), 403, and 404 (table not found) errors
    // Mark as unavailable to prevent repeated failing requests
    if (response.status === 401 || response.status === 404 || response.status === 403) {
      errorLogsUnavailable = true;
      return null;
    }

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to log error: ${response.status} ${errorText}`);
    }

    const data = await response.json();
    return Array.isArray(data) ? data[0] : data;
  } catch (error) {
    // Don't log errors from Supabase logging - just throw
    // This prevents infinite loops
    throw error;
  }
};

/**
 * Main error logging function
 * @param {Error|Object} error - The error object
 * @param {Object} context - Additional context (errorType, componentStack, apiEndpoint, etc.)
 */
export const logError = async (error, context = {}) => {
  // Prevent recursive error logging
  if (isLoggingError) {
    return;
  }

  // Skip if this is an error from the error logger itself
  const errorMsg = error?.message || error?.toString() || '';
  if (errorMsg.includes(ERROR_LOGGER_PREFIX) || 
      errorMsg.includes('Error in error logger') ||
      errorMsg.includes('Failed to log error') ||
      errorMsg.includes('Supabase unavailable')) {
    return;
  }

  isLoggingError = true;

  try {
    // Extract error information
    const errorMessage = error?.message || error?.toString() || 'Unknown error';
    const errorStack = error?.stack || null;
    const errorType = context.errorType || 'unhandled';
    const componentStack = context.componentStack || null;
    const apiEndpoint = context.apiEndpoint || null;
    const apiStatus = context.apiStatus || null;
    const requestData = context.requestData || null;

    // Get user context (with error handling)
    let userContext = { userId: null, userEmail: null, displayName: null };
    try {
      userContext = await getUserContext();
    } catch (userError) {
      // Silently fail - don't log user context errors
    }

    // Get device info
    let deviceInfo = {};
    try {
      deviceInfo = getDeviceInfo();
    } catch (deviceError) {
      // Silently fail - don't log device info errors
    }

    // Get current URL
    const url = window.location.href;

    // Determine severity
    const severity = context.severity || determineSeverity(error, errorType);

    // Prepare error data
    const errorData = {
      error_message: errorMessage.substring(0, 5000), // Limit message length
      error_stack: errorStack ? errorStack.substring(0, 10000) : null, // Limit stack length
      error_type: errorType,
      severity,
      user_id: userContext.userId,
      user_email: userContext.userEmail,
      url: url.substring(0, 2000), // Limit URL length
      user_agent: navigator.userAgent?.substring(0, 500) || null,
      device_info: deviceInfo,
      component_stack: componentStack ? componentStack.substring(0, 5000) : null,
      api_endpoint: apiEndpoint ? apiEndpoint.substring(0, 500) : null,
      api_status: apiStatus,
      request_data: requestData,
    };

    // Try to log to Supabase
    try {
      await logErrorToSupabase(errorData);
    } catch (supabaseError) {
      // If Supabase is unavailable, queue the error
      // Use original console.warn to avoid interception
      const originalWarn = console.warn;
      originalWarn.call(console, 'Supabase unavailable, queueing error');
      
      try {
        queueError(errorData);
      } catch (queueError) {
        // Silently fail if queueing also fails
      }
    }
  } catch (loggingError) {
    // Use original console methods to avoid interception
    const originalError = console.error;
    try {
      originalError.call(console, 'Error in error logger:', loggingError);
    } catch (e) {
      // Last resort - do nothing
    }
  } finally {
    isLoggingError = false;
  }
};

/**
 * Specialized function for API errors
 * @param {Error} error - The error object
 * @param {string} endpoint - API endpoint that failed
 * @param {Object} requestData - Request data (optional)
 */
export const logApiError = async (error, endpoint, requestData = null) => {
  const apiStatus = error?.status || error?.response?.status || null;
  
  await logError(error, {
    errorType: 'api',
    apiEndpoint: endpoint,
    apiStatus,
    requestData: requestData ? {
      method: requestData.method,
      body: requestData.body,
      headers: requestData.headers ? Object.keys(requestData.headers) : null, // Don't log sensitive headers
    } : null,
  });
};

/**
 * Log form validation errors
 * @param {Object} validationErrors - Object with field names as keys and error messages as values
 * @param {string} formName - Name/identifier of the form
 */
export const logValidationError = async (validationErrors, formName = 'unknown') => {
  const errorMessage = `Form validation failed: ${Object.keys(validationErrors).join(', ')}`;
  const error = new Error(errorMessage);
  
  await logError(error, {
    errorType: 'validation',
    severity: 'warning',
    requestData: {
      formName,
      validationErrors,
    },
  });
};

/**
 * Log user action errors (button clicks, form submissions, etc.)
 * @param {Error} error - The error object
 * @param {string} action - Description of the user action
 * @param {Object} context - Additional context
 */
export const logUserActionError = async (error, action = 'unknown', context = {}) => {
  const errorMessage = `User action error: ${action} - ${error.message || error.toString()}`;
  const errorObj = error instanceof Error ? error : new Error(errorMessage);
  
  await logError(errorObj, {
    errorType: 'user_action',
    severity: 'error',
    requestData: {
      action,
      ...context,
    },
  });
};

/**
 * Log resource loading errors (images, scripts, etc.)
 * @param {string} resourceUrl - URL of the resource that failed to load
 * @param {string} resourceType - Type of resource (image, script, stylesheet, etc.)
 */
export const logResourceError = async (resourceUrl, resourceType = 'unknown') => {
  const error = new Error(`Failed to load ${resourceType}: ${resourceUrl}`);
  
  await logError(error, {
    errorType: 'resource_loading',
    severity: 'warning',
    requestData: {
      resourceUrl,
      resourceType,
    },
  });
};

/**
 * Log storage errors (localStorage, sessionStorage)
 * @param {Error} error - The error object
 * @param {string} operation - Storage operation (get, set, remove, clear)
 * @param {string} key - Storage key (if applicable)
 */
export const logStorageError = async (error, operation = 'unknown', key = null) => {
  const errorMessage = `Storage ${operation} error${key ? ` for key: ${key}` : ''}`;
  const errorObj = error instanceof Error ? error : new Error(errorMessage);
  
  await logError(errorObj, {
    errorType: 'storage',
    severity: 'warning',
    requestData: {
      operation,
      key,
    },
  });
};

/**
 * Log route/navigation errors
 * @param {Error} error - The error object
 * @param {string} route - Route path
 */
export const logRouteError = async (error, route = 'unknown') => {
  const errorMessage = `Route error: ${route} - ${error.message || error.toString()}`;
  const errorObj = error instanceof Error ? error : new Error(errorMessage);
  
  await logError(errorObj, {
    errorType: 'route',
    severity: 'error',
    requestData: {
      route,
    },
  });
};

/**
 * Log authentication errors
 * @param {Error} error - The error object
 * @param {string} action - Authentication action (login, signup, logout, etc.)
 */
export const logAuthError = async (error, action = 'unknown') => {
  const errorMessage = `Auth ${action} error: ${error.message || error.toString()}`;
  const errorObj = error instanceof Error ? error : new Error(errorMessage);
  
  await logError(errorObj, {
    errorType: 'authentication',
    severity: 'error',
    requestData: {
      action,
    },
  });
};

/**
 * Initialize error logger
 * - Process any queued errors
 * - Set up online/offline listeners
 */
export const initializeErrorLogger = () => {
  // Process queue when app comes online
  if (navigator.onLine) {
    processErrorQueue();
  }

  // Listen for online event
  window.addEventListener('online', () => {
    processErrorQueue();
  });

  // Listen for offline event (for future enhancements)
  window.addEventListener('offline', () => {
    console.warn('App is offline - errors will be queued');
  });
};

export default {
  logError,
  logApiError,
  logValidationError,
  logUserActionError,
  logResourceError,
  logStorageError,
  logRouteError,
  logAuthError,
  initializeErrorLogger,
  processErrorQueue,
};
